'use client';

import React, { useMemo } from 'react';

import { INTERVAL_TIME } from '@workspace/constants';
import { HeroBannerData, HeroBannerSlide as HeroBannerSlideType } from '@workspace/interfaces';

import { HeroBannerControls } from './HeroBannerControls';
import { HeroBannerSlide } from './HeroBannerSlide';
import { HeroBannerSize } from '../hero-banner.types';
import { getHeroBannerMinHeightClass } from '../hero-banner-util';
import { cn } from '@workspace/lib';
import { useHeroBannerSlide } from '../useHeroBannerSlide';

type NormalizedHeroBannerSlide = Omit<HeroBannerSlideType, 'heading'> & {
  heading: {
    _type: string;
    fontSize?: 'h1' | 'h2' | 'h3';
    headingText: string;
  };
};
export interface HeroBannerProps {
  className?: string;
  size?: 'slim' | 'medium' | 'full';
  autoPlay?: boolean;
  interval?: number;
  // New preferred API
  slides?: NormalizedHeroBannerSlide[];
  // Legacy API (kept for backward compatibility)
  heroBannerData?: HeroBannerData;
  shareIcons?: any;
  disableDefaultFallback?: boolean;
  authorBlog?: string;
  publicDataBlog?: string;
}

export const HeroBanner = ({
  className = '',
  size,
  autoPlay = true,
  interval = INTERVAL_TIME,
  slides: propSlides,
  heroBannerData,
  shareIcons,
  authorBlog,
  publicDataBlog,
}: HeroBannerProps) => {
  const normalizedSlides: NormalizedHeroBannerSlide[] = useMemo(() => {
    if (Array.isArray(propSlides) && propSlides.length > 0) return propSlides as NormalizedHeroBannerSlide[];
    const rawSlides = (heroBannerData?.slides ?? []) as any[];
    return rawSlides.map((s, index) => {
      const heading =
        typeof s?.heading === 'string'
          ? { _type: 'heading', headingText: s.heading as string }
          : (s?.heading ?? { _type: 'heading', headingText: '' });
      return {
        _key: s?._key ?? `slide-${index}`,
        _type: s?._type ?? 'heroBannerSlide',
        tag: s?.tag,
        isVideo: Boolean(s?.isVideo),
        videoUrl: s?.videoUrl,
        heading,
        description: s?.description,
        contentPosition: s?.contentPosition,
        textColor: s?.textColor,
        image: s?.image,
        buttons: s?.buttons,
        socialLinks: s?.socialLinks,
        shareIcons: s?.shareIcons ?? shareIcons,
        authorBlog: s?.authorBlog ?? authorBlog,
        publicDataBlog: s?.publicDataBlog ?? publicDataBlog,
        isActive: s?.isActive,
        video: s?.video,
        showGradient: s?.showGradient,
      } as NormalizedHeroBannerSlide;
    });
  }, [propSlides, heroBannerData, shareIcons, authorBlog, publicDataBlog]);

  const visibleBlocks = useMemo(() => normalizedSlides, [normalizedSlides]);
  const total = useMemo(() => visibleBlocks.length, [visibleBlocks]);
  const hasSlides = useMemo(() => total > 0, [total]);

  const { emblaRef, selectedIndex, scrollNext, scrollPrev, scrollTo, scrollSnaps } = useHeroBannerSlide({
    slideCount: total,
    autoPlay: hasSlides ? autoPlay : false,
    interval,
  });

  const effectiveSize: HeroBannerSize = useMemo(() => {
    if (size) return size;
    const raw = (heroBannerData as any)?.heroBannerTypeStyle as string | undefined;
    if (typeof raw === 'string') {
      const v = raw.trim().toLowerCase();
      if (v === 'full') return 'full';
      if (v === 'slim') return 'slim';
    }
    return 'medium';
  }, [size, heroBannerData]);

  if (!hasSlides || heroBannerData?.isActive === false) return null;

  if (hasSlides) {
    const minHeightClass = getHeroBannerMinHeightClass(effectiveSize);

    const controlsTotal = scrollSnaps.length || total;

    return (
      <div className={cn('hero-banner-container relative w-full overflow-hidden', className)}>
        <div
          className={cn('relative h-auto', minHeightClass)}
          style={{
            touchAction: 'pan-y',
            WebkitUserSelect: 'none',
            userSelect: 'none',
            minHeight: '400px',
          }}
        >
          <div className="embla relative h-full">
            <div
              className="embla__viewport h-full w-full overflow-hidden touch-pan-y"
              ref={emblaRef}
              style={{
                touchAction: 'pan-y',
                WebkitUserSelect: 'none',
                userSelect: 'none',
              }}
            >
              <div className="embla__container flex h-full">
                {visibleBlocks.map((block, idx) => (
                  <HeroBannerSlide
                    key={block._key || `slide-${idx}`}
                    block={block}
                    isActive={idx === selectedIndex}
                    shouldPreloadImage={idx === 0}
                    size={effectiveSize}
                    className=""
                  />
                ))}
              </div>
            </div>
          </div>
        </div>

        {(effectiveSize === 'full' || effectiveSize === 'medium') && controlsTotal > 1 && (
          <HeroBannerControls
            total={controlsTotal}
            current={selectedIndex}
            onPrev={scrollPrev}
            onNext={scrollNext}
            onSlideChange={scrollTo}
            size={effectiveSize}
          />
        )}
      </div>
    );
  }

  return null;
};
